import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sparkles, Phone, MessageSquare, Bot, Brain, ShieldCheck, Calendar, Mic,
  Users, Workflow, Target, Headphones, Zap, BarChart3, FileText, Stethoscope,
  TrendingUp, ArrowRight, CheckCircle2, Building2, Clock, Star, PlayCircle,
  Rocket, Lock, Globe, DollarSign, Layers, GitBranch, Database, Fingerprint,
} from 'lucide-react';
import Logo from '../components/Logo.jsx';

const FEATURES = [
  {
    icon: Phone, color: 'from-cyan-400 to-blue-500',
    title: 'Power Dialer',
    blurb: 'Multi-line dialing with branded caller ID and click-to-call. Every completed call is recorded, then transcribed and summarized by AI automatically.',
  },
  {
    icon: MessageSquare, color: 'from-purple-400 to-pink-500',
    title: 'A2P-Compliant SMS + iMessage',
    blurb: '10DLC registered with a branded sender signature. Two-way SMS and iMessage (SendBlue), compliant drips, STOP/HELP keywords, and opt-out tracking.',
  },
  {
    icon: Brain, color: 'from-pink-500 to-rose-500',
    title: 'CIVIX Assistant',
    blurb: 'Your in-app AI co-pilot trained on YOUR product grids. Recommends carriers and drafts responses — never hallucinates competitors.',
  },
  {
    icon: Stethoscope, color: 'from-emerald-400 to-cyan-500',
    title: 'Underwriting Bot',
    blurb: 'Knows every carrier rule you upload. Asks the right health questions. Returns the right grid match in seconds.',
  },
  {
    icon: Calendar, color: 'from-amber-400 to-orange-500',
    title: 'Calendar That Books Itself',
    blurb: 'Two-way Google Calendar sync plus public booking pages and Meta Book Now — appointments push straight to your calendar automatically.',
  },
  {
    icon: Workflow, color: 'from-indigo-400 to-purple-500',
    title: 'Marketing Hub',
    blurb: 'Drips, automations, dispositions, preset texts, keywords, voicemails, and webhooks — build the follow-up once and let it run.',
  },
  {
    icon: Target, color: 'from-fuchsia-500 to-purple-600',
    title: 'Lead Distribution + Vendors',
    blurb: 'Round-robin routing, lead vendor intake mapping, and aged-lead recycling from the Lead Pool so no lead goes cold.',
  },
  {
    icon: Zap, color: 'from-rose-400 to-red-500',
    title: 'Meta + Google Ads',
    blurb: 'Native Meta Ads and Google Ads integrations with Ad Studio creative, launches, and performance tracking built in.',
  },
  {
    icon: Mic, color: 'from-cyan-400 to-purple-500',
    title: 'AI Roleplay Coach',
    blurb: 'Train new agents on real personas with scoring and transcripts. Onboarding cut from weeks to days.',
  },
  {
    icon: BarChart3, color: 'from-emerald-400 to-teal-500',
    title: 'Pipeline + Reports',
    blurb: 'Drag-and-drop sales pipeline with dispositions, agent leaderboard, My Digest, and ROI by campaign and vendor.',
  },
  {
    icon: Layers, color: 'from-blue-400 to-indigo-500',
    title: 'Unified Inbox',
    blurb: 'Every SMS conversation in one thread with auto-replies and keywords. STOP re-disclosure fires automatically on agent reassignment.',
  },
  {
    icon: Lock, color: 'from-slate-400 to-zinc-500',
    title: 'Built-in Compliance',
    blurb: 'A2P 10DLC brand signature, TCPA send-window enforcement, per-state dial caps, internal DNC suppression, and a full audit log.',
  },
];

const STATS = [
  { v: '1 OS', l: 'CRM, dialer & AI in one' },
  { v: '10DLC', l: 'A2P compliance built in' },
  { v: '2-way', l: 'Google Calendar sync' },
  { v: 'At-cost', l: 'usage, zero markup' },
];

const STACK_LOGOS = [
  'Twilio', 'OpenAI', 'SendBlue', 'Stripe', 'Google Calendar', 'Meta Ads', 'Google Ads',
];

export default function Landing() {
  const nav = useNavigate();

  // Subtle parallax on hero blobs
  const heroRef = useRef(null);
  useEffect(() => {
    function onMove(e) {
      const blobs = document.querySelectorAll('[data-blob]');
      const x = (e.clientX / window.innerWidth - 0.5) * 30;
      const y = (e.clientY / window.innerHeight - 0.5) * 30;
      blobs.forEach((b, i) => {
        const f = i % 2 === 0 ? 1 : -1;
        b.style.transform = `translate(${x * f}px, ${y * f}px)`;
      });
    }
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white overflow-x-hidden">
      {/* Ambient glows */}
      <div className="fixed inset-0 -z-10 pointer-events-none">
        <div data-blob className="absolute top-0 -left-40 w-[600px] h-[600px] rounded-full bg-purple-600/20 blur-[120px] transition-transform duration-700 ease-out" />
        <div data-blob className="absolute top-40 -right-40 w-[700px] h-[700px] rounded-full bg-cyan-500/15 blur-[120px] transition-transform duration-700 ease-out" />
        <div data-blob className="absolute top-[80vh] left-1/3 w-[500px] h-[500px] rounded-full bg-pink-500/10 blur-[120px] transition-transform duration-700 ease-out" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(10,10,15,0.4))]" />
        {/* grid texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }} />
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" showTagline={false} />
            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">Beta</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#features" className="hover:text-white transition">Features</a>
            <a href="#ai" className="hover:text-white transition">AI</a>
            <a href="#compliance" className="hover:text-white transition">Compliance</a>
            <a href="#pricing" className="hover:text-white transition">Pricing</a>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Link to="/login" className="hidden sm:inline-block px-4 py-2 text-sm text-white/80 hover:text-white whitespace-nowrap">Sign in</Link>
            <Link
              to="/signup"
              className="px-3.5 sm:px-4 py-2 text-sm font-medium rounded-full bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 transition shadow-lg shadow-purple-500/30 whitespace-nowrap"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main>
      {/* Hero */}
      <section ref={heroRef} className="relative px-6 pt-20 pb-32">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            Built for life insurance & final expense agencies
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] md:leading-none">
            Close more policies with{' '}
            <span className="bg-linear-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              AI that actually works.
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed md:leading-7">
            CIVIX is the all-in-one CRM, power dialer, AI assistant, and built-in compliance toolkit purpose-built for insurance agencies. Free during beta — pay only at-cost usage.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 transition shadow-xl shadow-purple-500/40 font-medium"
            >
              Start free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </Link>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 transition"
            >
              <PlayCircle className="w-4 h-4" />
              See what it does
            </a>
          </div>

          <div className="mt-8 text-xs text-white/40">
            No credit card required · Free during beta · Live in 2 minutes
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {STATS.map((s) => (
              <div key={s.l} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-linear-to-br from-white to-white/60 bg-clip-text text-transparent">{s.v}</div>
                <div className="text-xs text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating product mockup hint */}
        <div className="mt-20 relative max-w-5xl mx-auto">
          <div className="absolute inset-0 bg-linear-to-r from-purple-500/30 via-cyan-500/30 to-pink-500/30 blur-3xl opacity-60" />
          <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-2 shadow-2xl">
            <div className="rounded-xl bg-[#0d0d1a] aspect-video flex items-center justify-center overflow-hidden">
              <div className="text-center p-12">
                <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto opacity-90">
                  {[Bot, Phone, BarChart3, Calendar, Brain, ShieldCheck].map((Ic, i) => (
                    <div key={i} className="p-6 rounded-xl bg-linear-to-br from-white/5 to-white/2 border border-white/10 flex flex-col items-center gap-2">
                      <Ic className="w-7 h-7 text-cyan-300" />
                      <div className="h-1.5 rounded-full bg-white/10 w-16" />
                      <div className="h-1.5 rounded-full bg-white/5 w-12" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stack credibility */}
      <section className="px-6 py-12 border-y border-white/5 bg-white/1.5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center text-xs uppercase tracking-[0.2em] text-white/40 mb-6">Powered by best-in-class infrastructure</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            {STACK_LOGOS.map((s) => (
              <div key={s} className="text-white/60 font-medium text-sm">{s}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Big features grid */}
      <section id="features" className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">Everything you need</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
              An entire agency stack,{' '}
              <span className="bg-linear-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">in one place.</span>
            </h2>
            <p className="mt-4 text-white/60 max-w-2xl mx-auto">
              Stop stitching 12 tools together. CIVIX replaces your CRM, power dialer, AI assistant, texting platform, calendar, and reporting suite.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => {
              const Ic = f.icon;
              return (
                <div
                  key={f.title}
                  className="group relative rounded-2xl border border-white/10 bg-white/2 hover:bg-white/4 hover:border-white/20 transition p-6 overflow-hidden"
                >
                  <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-linear-to-br ${f.color} opacity-0 group-hover:opacity-20 blur-3xl transition`} />
                  <div className={`relative inline-flex w-11 h-11 items-center justify-center rounded-xl bg-linear-to-br ${f.color} mb-4 shadow-lg`}>
                    <Ic className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1.5">{f.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{f.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI section */}
      <section id="ai" className="px-6 py-24 relative">
        <div className="absolute inset-0 -z-10 bg-linear-to-b from-transparent via-purple-900/10 to-transparent" />
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block text-xs uppercase tracking-[0.2em] text-pink-300 mb-3">AI, but useful</div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight md:leading-none">
                Your underwriting bot only knows{' '}
                <span className="bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">your carriers.</span>
              </h2>
              <p className="mt-5 text-white/60 leading-relaxed">
                Most "AI for insurance" tools pull from the open internet and recommend carriers you don't even use. CIVIX's underwriting bot is locked to the grids and product PDFs you upload. Zero hallucination, zero off-roster recommendations.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Trained only on the carrier grids you upload',
                  'Underwriting Bot returns the right grid match in seconds',
                  'Roleplay coach scores agent practice calls',
                  'AI call summaries on every completed call',
                  'CIVIX Assistant drafts texts & recommends carriers',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-linear-to-br from-purple-500/30 to-pink-500/30 blur-3xl" />
              <div className="relative rounded-2xl border border-white/10 bg-[#0d0d1a]/80 backdrop-blur-sm p-6 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-linear-to-br from-cyan-400 to-purple-500 shrink-0 flex items-center justify-center"><Brain className="w-4 h-4" /></div>
                  <div className="flex-1 rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 p-3 text-sm">
                    65yo male, T2 diabetes onset 4 years ago, A1C of 7.1, no insulin. Best carrier?
                  </div>
                </div>
                <div className="flex items-start gap-3 flex-row-reverse">
                  <div className="w-8 h-8 rounded-full bg-linear-to-br from-pink-400 to-rose-500 shrink-0 flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
                  <div className="flex-1 rounded-2xl rounded-tr-sm bg-linear-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 p-3 text-sm">
                    <div className="font-semibold text-cyan-300 mb-1">ETHOS — Standard Tier</div>
                    Off your grid: A1C ≤ 7.5 with no insulin = Standard. SureBridge would also accept (Preferred Tobacco-Free) but ETHOS pays better commission for this band. Quote built — want me to text it?
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-linear-to-br from-cyan-400 to-purple-500 shrink-0 flex items-center justify-center"><Brain className="w-4 h-4" /></div>
                  <div className="flex-1 rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 p-3 text-sm">
                    Yes, send it.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance section */}
      <section id="compliance" className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute inset-0 bg-linear-to-br from-cyan-500/20 to-purple-500/20 blur-3xl" />
              <div className="relative rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="w-8 h-8 text-cyan-300" />
                  <div>
                    <div className="font-semibold">Compliance, on by default</div>
                    <div className="text-xs text-white/50">Enforced on every send</div>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5">
                    <span className="text-white/80">A2P 10DLC brand signature</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Signed</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5">
                    <span className="text-white/80">Send window · 8am–9pm by area code</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Enforced</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5">
                    <span className="text-white/80">Replied STOP · internal DNC</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-rose-500/15 text-rose-300 border border-rose-500/30">Suppressed</span>
                  </div>
                </div>
                <div className="mt-4 text-xs text-white/50">Brand signature, send-window checks, and opt-out suppression run automatically — no extra steps for your agents.</div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-block text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">Compliance built in</div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight md:leading-none">
                Outreach that plays{' '}
                <span className="bg-linear-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">by the rules.</span>
              </h2>
              <p className="mt-5 text-white/60 leading-relaxed">
                CIVIX bakes the tedious parts of TCPA and A2P into the platform. Your first message carries a registered 10DLC brand signature, sends respect per-area-code time windows and state dial caps, and any lead who replies STOP is suppressed on your internal DNC — automatically.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'A2P 10DLC registration & branded sender signature',
                  'TCPA send-window enforcement (8am–9pm by area code)',
                  'Per-state dial caps (FL, OK, MD, OR & more)',
                  'STOP opt-out handling + internal DNC suppression',
                  'Audit log of every send for compliance review',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
                    {line}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-white/40 leading-relaxed">
                Note: CIVIX enforces A2P/TCPA send controls and internal opt-out suppression. It does not scrub against the National DNC registry or litigator lists, and does not constitute legal advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-24 relative">
        <div className="absolute inset-0 -z-10 bg-linear-to-b from-transparent via-cyan-900/10 to-transparent" />
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">How it works</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Live in <span className="bg-linear-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">two minutes.</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: '01', t: 'Sign up', d: 'Drop in your A2P passcode from your manager and you\'re messaging in 30 seconds.', icon: Rocket },
              { n: '02', t: 'Import & route leads', d: 'Map lead-vendor fields on intake, then distribute round-robin to your team automatically.', icon: Database },
              { n: '03', t: 'Dial & close', d: 'Power dial your list, let AI summarize every call, and book straight into your synced Google Calendar without leaving the conversation.', icon: TrendingUp },
            ].map((s) => {
              const Ic = s.icon;
              return (
                <div key={s.n} className="relative rounded-2xl border border-white/10 bg-white/2 p-6">
                  <div className="text-5xl font-bold text-white/5 absolute top-4 right-5 select-none">{s.n}</div>
                  <Ic className="w-8 h-8 text-cyan-300 mb-4" />
                  <h3 className="font-semibold text-lg mb-2">{s.t}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block text-xs uppercase tracking-[0.2em] text-emerald-300 mb-3">Pricing</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
              Free during beta.{' '}
              <span className="bg-linear-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Always at-cost.</span>
            </h2>
            <p className="mt-4 text-white/60 max-w-2xl mx-auto">
              No subscription games. No per-seat markup. We pass through what Twilio, OpenAI, and SendBlue charge us. Nothing more.
            </p>
          </div>

          <div className="rounded-3xl border-2 border-emerald-500/40 bg-linear-to-br from-emerald-500/10 via-cyan-500/5 to-purple-500/10 p-8 md:p-12 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-xs text-emerald-300 mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Founding member access
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 tracking-tight">Beta is free. Forever-low pricing locked in.</h3>
              <p className="text-white/70 mb-8 max-w-xl">
                Sign up during the beta and you pay zero subscription fees while we test the platform together. You only cover your at-cost usage — calls, texts, AI tokens. When the beta wraps, founding members keep at-cost pricing for life.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-linear-to-r from-emerald-400 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 transition shadow-xl shadow-emerald-500/30 text-black font-semibold"
                >
                  Claim founding-member access
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 transition"
                >
                  Already have access? Sign in
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { v: '$0', l: 'CRM access' },
              { v: 'At cost', l: 'Calls + SMS' },
              { v: 'At cost', l: 'AI tokens' },
              { v: '$0', l: 'Setup & onboarding' },
            ].map((p) => (
              <div key={p.l} className="rounded-xl border border-white/10 bg-white/2 p-4">
                <div className="text-2xl font-bold">{p.v}</div>
                <div className="text-xs text-white/50 mt-1">{p.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-24">
        <div className="max-w-4xl mx-auto rounded-3xl border border-white/10 bg-linear-to-br from-purple-900/30 via-[#0d0d1a] to-pink-900/20 p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Ready to{' '}
              <span className="bg-linear-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">disrupt</span>{' '}your agency?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-8">
              Stop paying $500/seat for a CRM that hallucinates. Stop stitching a dozen tools together. Get on the system built by agents, for agents.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 transition shadow-xl shadow-purple-500/40 font-medium text-lg"
            >
              Start free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div className="flex items-center gap-3">
            <Logo size="sm" showTagline={false} />
            <span className="text-white/40">· CIVIX LLC</span>
          </div>
          <div className="flex gap-6">
            <Link to="/login" className="hover:text-white/80">Sign in</Link>
            <Link to="/signup" className="hover:text-white/80">Sign up</Link>
            <a href="mailto:contact@civixcrm.com" className="hover:text-white/80">Contact</a>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} CIVIX. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
